/**
 * What: Maps thrown errors and API failures onto wording that is safe to show a merchant.
 * Transport / data-flow: Pure functions over already-fetched errors; reads @testfeed/product-language copy.
 *                        No fetch, no Shopify calls, no D1.
 *
 * Why this exists: `payload.error` from the Worker was rendered verbatim. Those strings are
 * developer text — the set includes raw `D1_ERROR:` output, an internal file path, and an
 * API key — so displaying an arbitrary error message is an information-disclosure risk, not
 * just a copy-hygiene one. Nothing here ever returns a raw message; a message is displayable
 * only when it was deliberately written for merchants and thrown as MerchantFacingError.
 */
import type { ZodType } from 'zod'
import { shopifyComponentCopy } from '@testfeed/product-language'

/** An error carrying wording deliberately written for merchants, safe to display as-is. */
export class MerchantFacingError extends Error {
  readonly isMerchantFacing = true as const
  readonly status?: number
  readonly code?: string
  readonly reconnectUrl?: string
  readonly correlationId?: string

  constructor(
    merchantMessage: string,
    options?: {
      cause?: unknown
      status?: number
      code?: string | null
      reconnectUrl?: string | null
      correlationId?: string | null
    },
  ) {
    super(merchantMessage, options)
    this.name = 'MerchantFacingError'
    if (typeof options?.status === 'number') this.status = options.status
    if (options?.reconnectUrl) this.reconnectUrl = options.reconnectUrl
    if (typeof options?.code === 'string' && options.code.length > 0) this.code = options.code
    if (options?.correlationId) this.correlationId = options.correlationId
  }
}

/** Narrows unknown catch values to errors whose message is merchant-safe. */
export function isMerchantFacingError(value: unknown): value is MerchantFacingError {
  return value instanceof MerchantFacingError
}

const SCOPE_GUARD_MESSAGE_PATTERN = /Verified Shopify|shop scope/i

/**
 * True for pre-flight shop-scope rejections (unavailable scope, racing shop change).
 * Single choke point for the reject-before-any-request invariant: reporting one of
 * these would send an authenticated request after a scope mismatch, so callers skip
 * diagnostics when this returns true. Keep in sync with the guard messages thrown in
 * mutation/query fetch functions.
 */
export function isScopeGuardRejection(error: unknown): boolean {
  return error instanceof Error && SCOPE_GUARD_MESSAGE_PATTERN.test(error.message)
}

/**
 * Resolves any caught value to merchant-safe wording.
 *
 * A raw `error.message` is never returned. Pass the wording you want the merchant to read
 * as `fallback`, sourced from @testfeed/product-language.
 */
export function resolveMerchantErrorMessage(error: unknown, fallback: string): string {
  if (isMerchantFacingError(error)) return error.message
  return fallback
}

/**
 * Keeps the underlying failure readable for logs and observability without putting it on
 * screen. Returns null when there is nothing useful to record.
 */
export function describeErrorForDiagnostics(error: unknown): string | null {
  if (isMerchantFacingError(error) && error.code) return `${error.name} [${error.code}]: ${error.message}`
  if (error instanceof Error) return `${error.name}: ${error.message}`
  if (typeof error === 'string' && error.trim().length > 0) return error
  return null
}

/** Formats the first issue of a failed Zod parse for diagnostics. Schema identity comes from the caller, so the record names the contract that rejected the payload. */
export function firstZodIssueMessage(
  schemaId: string,
  error: { issues: Array<{ path: Array<PropertyKey>; message: string }> },
): string {
  const [first] = error.issues
  if (!first) return `${schemaId}: invalid payload`
  const path = first.path.length > 0 ? first.path.map((segment) => String(segment)).join('.') : '(root)'
  return `${schemaId}: ${path}: ${first.message}`
}

/** Validates an unknown payload against a Zod schema, throwing an enriched diagnostic naming the contract and first issue path. The blessed pattern for validating remote payloads in page code. */
export function parseContractPayload<T>(schemaId: string, schema: ZodType<T>, value: unknown): T {
  const parsed = schema.safeParse(value)
  if (!parsed.success) {
    throw new Error(firstZodIssueMessage(schemaId, parsed.error))
  }
  return parsed.data
}

/** Generic wording for a failure with no more specific message available. */
export function genericMerchantErrorMessage(): string {
  return shopifyComponentCopy.shopifyApiErrors.somethingWentWrong003
}
